import { StyleSheet, css } from 'aphrodite'
import { useEffect, useMemo, useState } from 'react'
import {
  DISPATCH_TEMPLATE_CONFIG,
  type ProjectTemplateRecipientType,
} from '../../config/templateConfig'
import { useUser } from '../../contexts/UserContext'
import { useSaveToDirectory } from '../../hooks/useSaveToDirectory'
import { userService } from '../../services/userService'
import type { Project } from '../../types/project'
import type { Contact, User } from '../../types/user'
import { StakeholderType, UserType } from '../../types/user'
import type { WorkOrder } from '../../types/workorder'
import Input from '../Input'
import MultiSelect, { type Option } from '../MultiSelect'
import SaveToDirectoryDialog from '../contact/SaveToDirectoryDialog'

interface TemplateContactInfoStepProps {
  data: Partial<WorkOrder>
  onUpdate: (data: Partial<WorkOrder>) => void
  project: Project | null
  readOnly?: boolean
  onValidationChange?: (isValid: boolean) => void
  showErrors?: boolean
  templateType: ProjectTemplateRecipientType
}

interface DisplayContact {
  name: string
  email?: string
  phone?: string
}

const TemplateContactInfoStep: React.FC<TemplateContactInfoStepProps> = ({
  data,
  onUpdate,
  project,
  readOnly = false,
  onValidationChange,
  showErrors = false,
  templateType,
}) => {
  const { user: currentUser } = useUser()
  const [dispatchers, setDispatchers] = useState<
    {
      id: number
      name: string
      email: string
      phone?: string
      isTeamMember: boolean
    }[]
  >([])
  const templateConfig = DISPATCH_TEMPLATE_CONFIG[templateType]

  // Track if dispatcher has been confirmed (selected from dropdown or manually entered and confirmed)
  const [dispatcherConfirmed, setDispatcherConfirmed] = useState(false)

  // Save-to-directory workflow for dispatcher
  const {
    showDialog: showDispatcherDialog,
    pendingContact: pendingDispatcherContact,
    checkAndPrompt: checkDispatcherSaveToDirectory,
    handleConfirm: handleDispatcherDialogConfirm,
    handleCancel: handleDispatcherDialogCancel,
  } = useSaveToDirectory()

  // Get contractor info from project
  const contractorInfo = useMemo((): DisplayContact | null => {
    if (!project) return null

    // Check if project has contractors or assigned_contractors
    if (project.contractors && project.contractors.length > 0) {
      const contractor = project.contractors[0]
      return {
        name: `${contractor.first_name} ${contractor.last_name}`.trim(),
        email: contractor.email,
      }
    }

    // Check created_by as fallback for contractor
    if (
      project.created_by &&
      typeof project.created_by === 'object' &&
      project.created_by.current_profile?.stakeholder_type ===
        StakeholderType.CONTRACTOR
    ) {
      return {
        name: `${project.created_by.first_name} ${project.created_by.last_name}`.trim(),
        email: project.created_by.email,
      }
    }

    return null
  }, [project])

  // Get recipient info (Broker, Third Party Contractor, or Sub Hauler) from project
  const recipientInfo = useMemo((): DisplayContact | null => {
    if (!project) return null

    if (templateType === 'BROKER') {
      if (project.brokers && project.brokers.length > 0) {
        const broker = project.brokers[0]
        return {
          name: `${broker.first_name} ${broker.last_name}`.trim(),
          email: broker.email,
        }
      }
    } else if (templateType === 'THIRD_PARTY_CONTRACTOR') {
      if (
        project.third_party_contractors &&
        project.third_party_contractors.length > 0
      ) {
        const contractor = project.third_party_contractors[0]
        return {
          name: `${contractor.first_name} ${contractor.last_name}`.trim(),
          email: contractor.email,
        }
      }
    } else if (templateType === 'SUB_HAULER') {
      if (project.sub_haulers && project.sub_haulers.length > 0) {
        const subHauler = project.sub_haulers[0]
        return {
          name: `${subHauler.first_name} ${subHauler.last_name}`.trim(),
          email: subHauler.email,
        }
      }
    }

    return null
  }, [project, templateType])

  // Get site supervisor info from project
  const siteSupervisorInfo = useMemo((): DisplayContact | null => {
    if (!project) return null

    // Check for form-based site supervisor fields (from template project terms)
    if (
      project.site_supervisor_first_name &&
      project.site_supervisor_last_name
    ) {
      return {
        name: `${project.site_supervisor_first_name} ${project.site_supervisor_last_name}`.trim(),
        phone: project.site_supervisor_phone,
        email: project.site_supervisor_email,
      }
    }

    // Check for site_supervisors array
    if (project.site_supervisors && project.site_supervisors.length > 0) {
      const supervisor = project.site_supervisors[0]
      return {
        name: `${supervisor.first_name} ${supervisor.last_name}`.trim(),
        email: supervisor.email,
        phone: supervisor.phone_number,
      }
    }

    // Check for site_supervisor_users array
    if (
      project.site_supervisor_users &&
      project.site_supervisor_users.length > 0
    ) {
      const supervisor = project.site_supervisor_users[0]
      return {
        name: `${supervisor.first_name} ${supervisor.last_name}`.trim(),
        email: supervisor.email,
        phone: supervisor.phone_number,
      }
    }

    return null
  }, [project])

  // Set template_recipient_type on mount (no auto-prefill of dispatcher)
  useEffect(() => {
    if (templateType && !data.template_recipient_type) {
      onUpdate({
        template_recipient_type: templateType,
      })
    }
  }, [templateType, data.template_recipient_type, onUpdate])

  // If data exists (from a previous session or editing), auto-confirm
  useEffect(() => {
    if (
      !dispatcherConfirmed &&
      data.contractor_dispatcher_id &&
      (data.contractor_dispatcher_first_name ||
        data.contractor_dispatcher_last_name)
    ) {
      setDispatcherConfirmed(true)
    }
  }, [
    dispatcherConfirmed,
    data.contractor_dispatcher_id,
    data.contractor_dispatcher_first_name,
    data.contractor_dispatcher_last_name,
  ])

  // Fetch dispatchers for selection
  useEffect(() => {
    const fetchDispatchers = async () => {
      try {
        const [contacts, teamMembers] = await Promise.all([
          userService.getContacts(),
          userService.getTeamMembers(),
        ])

        // Get dispatcher contacts
        const dispatcherContacts = contacts
          .filter((c) => c.contact_object.user_type === UserType.DISPATCHER)
          .map((contact) => ({
            id: contact.contact_object.user_id || contact.id,
            name: contact.contact_object.name || contact.contact_object.email,
            email: contact.contact_object.email,
            phone: contact.contact_object.phone_number,
            isTeamMember: false,
          }))

        // Get team members with DISPATCHER user type
        const dispatcherTeamMembers = teamMembers
          .filter((user: User) => user.user_type === UserType.DISPATCHER)
          .map((user: User) => ({
            id: user.id,
            name: `${user.first_name} ${user.last_name}`.trim() || user.email,
            email: user.email,
            phone: user.phone_number,
            isTeamMember: true,
          }))

        // Combine and deduplicate
        const allDispatchers = [...dispatcherTeamMembers, ...dispatcherContacts]
        const uniqueDispatchers = allDispatchers.filter(
          (dispatcher, index, self) =>
            index === self.findIndex((d) => d.id === dispatcher.id),
        )

        setDispatchers(uniqueDispatchers)
      } catch (error) {
        console.error('Failed to fetch dispatchers:', error)
      }
    }

    fetchDispatchers()
  }, [])

  // Validation
  const isValid = useMemo(() => {
    // Check contractor's dispatcher fields are filled
    const hasDispatcherFirstName =
      !!data.contractor_dispatcher_first_name?.trim()
    const hasDispatcherLastName = !!data.contractor_dispatcher_last_name?.trim()
    const hasDispatcherContact =
      !!data.contractor_dispatcher_phone?.trim() ||
      !!data.contractor_dispatcher_email?.trim()

    return (
      hasDispatcherFirstName && hasDispatcherLastName && hasDispatcherContact
    )
  }, [
    data.contractor_dispatcher_first_name,
    data.contractor_dispatcher_last_name,
    data.contractor_dispatcher_phone,
    data.contractor_dispatcher_email,
  ])

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid)
    }
  }, [isValid, onValidationChange])

  const hasDispatcherFirstNameError =
    showErrors && !data.contractor_dispatcher_first_name?.trim()
  const hasDispatcherLastNameError =
    showErrors && !data.contractor_dispatcher_last_name?.trim()
  const hasDispatcherContactError =
    showErrors &&
    !data.contractor_dispatcher_phone?.trim() &&
    !data.contractor_dispatcher_email?.trim()

  // Convert dispatchers to options for MultiSelect
  const dispatcherOptions = useMemo(
    () =>
      dispatchers.map((dispatcher) => ({
        label: `${dispatcher.name} (${dispatcher.email})${dispatcher.isTeamMember ? ' - Team Member' : ''}`,
        value: dispatcher.id.toString(),
        name: dispatcher.name,
        email: dispatcher.email,
      })),
    [dispatchers],
  )

  const handleDispatcherSelect = (selected: Option | Option[] | null) => {
    const selectedOption = Array.isArray(selected) ? selected[0] : selected
    if (!selectedOption) return

    const dispatcher = dispatchers.find(
      (d) => d.id.toString() === selectedOption.value,
    )
    if (dispatcher) {
      const nameParts = dispatcher.name.split(' ')
      onUpdate({
        contractor_dispatcher_first_name: nameParts[0] || '',
        contractor_dispatcher_last_name: nameParts.slice(1).join(' ') || '',
        contractor_dispatcher_email: dispatcher.email,
        contractor_dispatcher_phone: dispatcher.phone || '',
        contractor_dispatcher_id: dispatcher.id,
      })
      setDispatcherConfirmed(true)

      // Check if we should prompt to save to directory (if not the current user)
      if (dispatcher.email && dispatcher.id !== currentUser?.id) {
        checkDispatcherSaveToDirectory({
          email: dispatcher.email,
          name: dispatcher.name,
          phoneNumber: dispatcher.phone,
        })
      }
    }
  }

  // Handle clearing the dispatcher to allow re-selection
  const handleClearDispatcher = () => {
    onUpdate({
      contractor_dispatcher_first_name: '',
      contractor_dispatcher_last_name: '',
      contractor_dispatcher_email: '',
      contractor_dispatcher_phone: '',
      contractor_dispatcher_id: undefined,
    })
    setDispatcherConfirmed(false)
  }

  // Check if dispatcher has data
  const hasDispatcherData =
    data.contractor_dispatcher_first_name ||
    data.contractor_dispatcher_last_name

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>CONTACT INFORMATION</h3>

      {/* Contractor Section (Read-only from project) */}
      <div className={css(styles.infoSection)}>
        <div className={css(styles.infoRow)}>
          <span className={css(styles.infoLabel)}>Contractor</span>
          <span className={css(styles.infoValue)}>
            {contractorInfo?.name || 'Not specified'}
          </span>
        </div>
      </div>

      {/* Recipient Section (Broker/Third Party Contractor/Sub Hauler from project) */}
      <div className={css(styles.infoSection)}>
        <div className={css(styles.infoRow)}>
          <span className={css(styles.infoLabel)}>
            {templateConfig.contactLabel}
          </span>
          <span className={css(styles.infoValue)}>
            {recipientInfo?.name || 'Not specified'}
          </span>
        </div>
      </div>

      {/* Site Supervisor Section (Read-only from project) */}
      <div className={css(styles.infoSection)}>
        <h4 className={css(styles.subSectionTitle)}>Site Supervisor</h4>
        <div className={css(styles.infoRow)}>
          <span className={css(styles.infoLabel)}>Name</span>
          <span className={css(styles.infoValue)}>
            {siteSupervisorInfo?.name || 'Not specified'}
          </span>
        </div>
        {(siteSupervisorInfo?.phone || siteSupervisorInfo?.email) && (
          <div className={css(styles.infoRow)}>
            <span className={css(styles.infoLabel)}>Contact</span>
            <span className={css(styles.infoValue)}>
              {siteSupervisorInfo?.phone && siteSupervisorInfo?.email
                ? `${siteSupervisorInfo.phone} / ${siteSupervisorInfo.email}`
                : siteSupervisorInfo?.phone || siteSupervisorInfo?.email}
            </span>
          </div>
        )}
      </div>

      {/* Contractor's Dispatcher Section (Prefilled with current user, editable) */}
      <div className={css(styles.dispatcherSection)}>
        <h4 className={css(styles.subSectionTitle)}>
          Contractor's Dispatcher *
        </h4>

        {dispatcherConfirmed && hasDispatcherData ? (
          /* Display in confirmed view with read-only name and removable contact */
          <div className={css(styles.confirmedDispatcher)}>
            {/* Name (read-only with lock icon) */}
            <div className={css(styles.dispatcherField)}>
              <div className={css(styles.fieldLabel)}>Name</div>
              <div className={css(styles.lockedValue)}>
                <i className="fa-solid fa-lock" />
                <span>
                  {`${data.contractor_dispatcher_first_name || ''} ${data.contractor_dispatcher_last_name || ''}`.trim()}
                </span>
              </div>
            </div>

            {/* Email (removable) */}
            <div className={css(styles.dispatcherField)}>
              <div className={css(styles.fieldLabel)}>Email</div>
              {data.contractor_dispatcher_email ? (
                <div className={css(styles.removableValue)}>
                  <a
                    href={`mailto:${data.contractor_dispatcher_email}`}
                    className={css(styles.clickableLink)}
                  >
                    {data.contractor_dispatcher_email}
                  </a>
                  {!readOnly && (
                    <button
                      type="button"
                      className={css(styles.removeItemButton)}
                      onClick={() =>
                        onUpdate({ contractor_dispatcher_email: '' })
                      }
                      title="Remove email"
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  )}
                </div>
              ) : (
                <span className={css(styles.emptyValue)}>Not set</span>
              )}
            </div>

            {/* Phone (removable) */}
            <div className={css(styles.dispatcherField)}>
              <div className={css(styles.fieldLabel)}>Phone</div>
              {data.contractor_dispatcher_phone ? (
                <div className={css(styles.removableValue)}>
                  <a
                    href={`tel:${data.contractor_dispatcher_phone}`}
                    className={css(styles.clickableLink)}
                  >
                    {data.contractor_dispatcher_phone}
                  </a>
                  {!readOnly && (
                    <button
                      type="button"
                      className={css(styles.removeItemButton)}
                      onClick={() =>
                        onUpdate({ contractor_dispatcher_phone: '' })
                      }
                      title="Remove phone"
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  )}
                </div>
              ) : (
                <span className={css(styles.emptyValue)}>Not set</span>
              )}
            </div>

            {/* Change button */}
            {!readOnly && (
              <button
                type="button"
                className={css(styles.changeButton)}
                onClick={handleClearDispatcher}
              >
                Change Dispatcher
              </button>
            )}

            {hasDispatcherContactError && (
              <div className={css(styles.errorMessage)}>
                Please provide either a phone number or email address.
              </div>
            )}
          </div>
        ) : (
          /* Form mode for entering/selecting dispatcher */
          <>
            {/* Quick select from existing dispatchers */}
            <div className={css(styles.formGroup)}>
              <label className={css(styles.label)}>
                Select from existing dispatchers (optional)
              </label>
              <MultiSelect
                isDisabled={readOnly}
                placeholder="Select a dispatcher to autofill"
                options={dispatcherOptions}
                value={null}
                onChange={handleDispatcherSelect}
              />
            </div>

            <div className={css(styles.gridContainer)}>
              <div className={css(styles.formGroup)}>
                <label className={css(styles.label)}>First Name *</label>
                <Input
                  disabled={readOnly}
                  placeholder="Enter first name"
                  value={data.contractor_dispatcher_first_name || ''}
                  onChange={(e) =>
                    onUpdate({
                      contractor_dispatcher_first_name: e.target.value,
                    })
                  }
                  error={
                    hasDispatcherFirstNameError
                      ? 'First name is required'
                      : undefined
                  }
                />
              </div>

              <div className={css(styles.formGroup)}>
                <label className={css(styles.label)}>Last Name *</label>
                <Input
                  disabled={readOnly}
                  placeholder="Enter last name"
                  value={data.contractor_dispatcher_last_name || ''}
                  onChange={(e) =>
                    onUpdate({
                      contractor_dispatcher_last_name: e.target.value,
                    })
                  }
                  error={
                    hasDispatcherLastNameError
                      ? 'Last name is required'
                      : undefined
                  }
                />
              </div>

              <div className={css(styles.formGroup)}>
                <label className={css(styles.label)}>Phone Number</label>
                <Input
                  disabled={readOnly}
                  placeholder="Enter phone number"
                  value={data.contractor_dispatcher_phone || ''}
                  onChange={(e) =>
                    onUpdate({ contractor_dispatcher_phone: e.target.value })
                  }
                />
              </div>

              <div className={css(styles.formGroup)}>
                <label className={css(styles.label)}>Email Address</label>
                <Input
                  disabled={readOnly}
                  placeholder="Enter email address"
                  type="email"
                  value={data.contractor_dispatcher_email || ''}
                  onChange={(e) =>
                    onUpdate({ contractor_dispatcher_email: e.target.value })
                  }
                  onBlur={(e) => {
                    // Check if we should prompt to save to directory
                    const email = e.target.value?.trim()
                    if (email && email !== currentUser?.email) {
                      const name =
                        `${data.contractor_dispatcher_first_name || ''} ${data.contractor_dispatcher_last_name || ''}`.trim()
                      checkDispatcherSaveToDirectory({
                        email,
                        name: name || undefined,
                        phoneNumber:
                          data.contractor_dispatcher_phone || undefined,
                      })
                    }
                  }}
                />
              </div>
            </div>

            {/* Confirm button to lock in the dispatcher */}
            {hasDispatcherData && !readOnly && (
              <button
                type="button"
                className={css(styles.confirmButton)}
                onClick={() => setDispatcherConfirmed(true)}
                disabled={
                  !data.contractor_dispatcher_first_name?.trim() ||
                  !data.contractor_dispatcher_last_name?.trim()
                }
              >
                Confirm Dispatcher
              </button>
            )}

            {hasDispatcherContactError && (
              <div className={css(styles.errorMessage)}>
                Please provide either a phone number or email address.
              </div>
            )}
          </>
        )}
      </div>

      {/* Save to Directory Dialog for Dispatcher */}
      {showDispatcherDialog && pendingDispatcherContact && (
        <SaveToDirectoryDialog
          email={pendingDispatcherContact.email}
          name={pendingDispatcherContact.name}
          phoneNumber={pendingDispatcherContact.phoneNumber}
          stakeholderTypes={[StakeholderType.DISPATCHER]}
          onConfirm={handleDispatcherDialogConfirm}
          onSkip={handleDispatcherDialogCancel}
        />
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: '20px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '20px',
  },
  subSectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '16px',
    color: '#374151',
  },
  infoSection: {
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    ':last-child': {
      marginBottom: 0,
    },
  },
  infoLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#6B7280',
    minWidth: '120px',
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#111827',
  },
  dispatcherSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #E5E7EB',
  },
  confirmedDispatcher: {
    backgroundColor: '#F0F9FF',
    border: '1px solid #BAE6FD',
    borderRadius: '8px',
    padding: '16px',
  },
  dispatcherField: {
    marginBottom: '12px',
    ':last-of-type': {
      marginBottom: 0,
    },
  },
  fieldLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  lockedValue: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#374151',
    padding: '8px 12px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
    ':nth-child(1n) > i': {
      fontSize: '10px',
      color: '#9CA3AF',
    },
  },
  removableValue: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    fontSize: '14px',
    padding: '8px 12px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
  },
  emptyValue: {
    fontSize: '14px',
    color: '#9CA3AF',
    fontStyle: 'italic',
    padding: '8px 12px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
  },
  clickableLink: {
    color: '#2563EB',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  removeItemButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#9CA3AF',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#FEE2E2',
      color: '#DC2626',
    },
  },
  changeButton: {
    marginTop: '16px',
    padding: '8px 16px',
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  confirmButton: {
    marginTop: '16px',
    padding: '8px 16px',
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    ':disabled': {
      backgroundColor: '#9CA3AF',
      cursor: 'not-allowed',
    },
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px 24px',
    marginTop: '16px',
  },
  formGroup: {
    marginBottom: '0',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
  },
  errorMessage: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '8px',
    fontWeight: 500,
  },
})

export default TemplateContactInfoStep
